import { FormattedMessage, useIntl } from 'react-intl';
import Button from '../core/Button';
import { IconPlus } from '@/icons';
import { FC, useCallback, useMemo, useState } from 'react';
import { DepartmentCreateForm } from './DepartmentCreateForm';
import { DialogWindow } from '../Dialog';
import { DeleteConfirmationForm } from '../DeleteConfirmationForm';
import { toast } from '@/hooks/useToast';
import {
  getGetCurrentDepartmentTreeQueryKey,
  useDeleteDepartmentByIDHook,
  useListAllpayUsers,
  useListCardCredits,
} from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { FormSection } from '../FormSection';
import { useTenantID } from '@/hooks/useTenantID';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import { refreshDepartmentAccess } from '@/stores/auth';

interface DepartmentAdvancedProps {
  departmentId: string;
  parentDepartmentId: string;
  hasChildDepartments: boolean;
}

export const DepartmentAdvanced: FC<DepartmentAdvancedProps> = ({
  departmentId,
  parentDepartmentId,
  hasChildDepartments,
}) => {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col py-4 @container md:py-8">
      <div className="mb-4 flex w-full flex-row justify-between">
        <h2 className="w-full">
          <FormattedMessage defaultMessage="Avancerat" />
        </h2>
      </div>
      <div className="flex w-full flex-col gap-8">
        {hasDepartmentWriteAccess(departmentId) && (
          <Button
            startIcon={<IconPlus />}
            onClick={() => setOpen(true)}
            variant="outline"
          >
            <FormattedMessage defaultMessage="Ny underavdelning" />
          </Button>
        )}
        {parentDepartmentId && (
          <DeleteDepartmentSection
            departmentId={departmentId}
            parentDepartmentId={parentDepartmentId}
            hasChildDepartments={hasChildDepartments}
          />
        )}
      </div>
      <DialogWindow
        title={formatMessage({ defaultMessage: 'Skapa ny underavdelning' })}
        content={
          <DepartmentCreateForm
            departmentId={departmentId}
            onOpenChange={setOpen}
          />
        }
        onOpenChange={setOpen}
        open={open}
      />
    </div>
  );
};

const DeleteDepartmentSection = ({
  departmentId,
  parentDepartmentId,
  hasChildDepartments,
}: {
  departmentId: string;
  parentDepartmentId: string;
  hasChildDepartments: boolean;
}) => {
  const deleteDepartment = useDeleteDepartmentByIDHook();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const tenantId = useTenantID();

  const { data: allpayUsers = [] } = useListAllpayUsers({
    department_id: departmentId,
  });
  const { data: cardCredits = [] } = useListCardCredits();

  const departmentCardCredits = useMemo(() => {
    if (!cardCredits || cardCredits.length === 0) return [];

    return cardCredits.filter(
      (credit) => credit.department_id === departmentId,
    );
  }, [cardCredits, departmentId]);

  const canDelete =
    !hasChildDepartments &&
    (!allpayUsers || allpayUsers?.length === 0) &&
    departmentCardCredits?.length === 0;

  const handleDelete = useCallback(() => {
    deleteDepartment(departmentId).then(async () => {
      toast.success(
        {
          title: formatMessage({
            defaultMessage: 'Avdelningen raderades',
          }),
        },
        { autoClose: 3000 },
      );
      await queryClient.invalidateQueries({
        queryKey: getGetCurrentDepartmentTreeQueryKey(),
      });
      await refreshDepartmentAccess();
      await navigate({
        to: '/$tenantId/departments',
        params: { tenantId },
        search: { departmentId: parentDepartmentId },
        replace: false,
      });
    });
  }, [
    deleteDepartment,
    departmentId,
    formatMessage,
    queryClient,
    navigate,
    parentDepartmentId,
    tenantId,
  ]);

  if (!hasDepartmentWriteAccess(parentDepartmentId)) {
    return (
      <FormSection
        title={formatMessage({ defaultMessage: 'Radera' })}
        editMode={false}
      >
        <FormattedMessage defaultMessage="Du har inte tillgång till att radera denna avdelning. Enbart en administratör med skrivrättighet för avdelningen ovanför kan ta bort den." />
      </FormSection>
    );
  }
  if (!canDelete) {
    return (
      <FormSection
        title={formatMessage({ defaultMessage: 'Radera' })}
        editMode={false}
      >
        <FormattedMessage defaultMessage="För att ta bort denna avdelning måste du först ta bort alla underavdelningar samt alla tjänsteresor i app och på kort på resenärerna." />
      </FormSection>
    );
  }

  return (
    <DeleteConfirmationForm
      title={formatMessage({ defaultMessage: 'Radera' })}
      warningMessage={
        <FormattedMessage defaultMessage="All data för avdelningen kommer att raderas och kan inte ångras. Resenärer som är medlemmar i enbart denna avdelningen kommer också tas bort." />
      }
      canDelete={true}
      onDelete={handleDelete}
    />
  );
};
